import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import { formatDateDisplay } from '../utils/formatDate';
import './Caterers.css';

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

const PAYMENT_MODES = ['Cash', 'Card', 'Online', 'Cheque', 'Store-1-QR', 'Store-2-QR'];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CatererAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('events'); // events | statement
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [statement, setStatement] = useState(null);
  const [stmtFrom, setStmtFrom] = useState('');
  const [stmtTo, setStmtTo] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: '',
    payment_mode: 'Cash',
    payment_date: todayIso(),
    notes: ''
  });
  const [msg, setMsg] = useState(null);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const { data } = await api.get(`/caterers/${id}/account`);
      setAccount(data);
      setPayForm((p) => ({
        ...p,
        amount: data.summary?.due > 0 ? String(data.summary.due) : ''
      }));
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Could not load caterer account' });
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadStatement = useCallback(async () => {
    try {
      const { data } = await api.get(`/caterers/${id}/statement`, {
        params: {
          from: stmtFrom || undefined,
          to: stmtTo || undefined
        }
      });
      setStatement(data);
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Could not load statement' });
    }
  }, [id, stmtFrom, stmtTo]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (tab === 'statement') loadStatement();
  }, [tab, loadStatement]);

  const openEvents = useMemo(
    () => (account?.events || []).filter((e) => e.dueAmount > 0),
    [account]
  );

  const submitPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payForm.amount);
    if (!(amount > 0)) {
      alert('Enter a valid amount');
      return;
    }
    setPayBusy(true);
    try {
      const { data } = await api.post(`/caterers/${id}/payments`, {
        amount,
        payment_mode: payForm.payment_mode,
        payment_date: payForm.payment_date,
        notes: payForm.notes || undefined
      });
      setPayOpen(false);
      setMsg({
        type: 'ok',
        text: `Recorded ${formatInr(amount)} · allocated to ${data.allocations?.length || 0} event(s)`
      });
      await loadAccount();
      if (tab === 'statement') await loadStatement();
    } catch (err) {
      alert(err.response?.data?.error || 'Payment failed');
    } finally {
      setPayBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="caterers-page">
        <PageNavbar />
        <div className="container">Loading caterer account…</div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="caterers-page">
        <PageNavbar />
        <div className="container">
          <p className="caterers-msg caterers-msg--err">{msg?.text || 'Not found'}</p>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/caterers')}>
            Back to caterers
          </button>
        </div>
      </div>
    );
  }

  const { caterer, summary, events } = account;

  return (
    <div className="caterers-page">
      <PageNavbar />
      <div className="container">
        <div className="caterers-header">
          <div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/caterers')}>
              ← Caterers
            </button>
            <h1 style={{ marginTop: 10 }}>{caterer.name}</h1>
            <p className="caterers-hint" style={{ marginBottom: 0 }}>
              {[caterer.phone, caterer.supervisor_name, caterer.address].filter(Boolean).join(' · ') ||
                'Caterer statement of account'}
            </p>
          </div>
          <div className="caterer-account-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!summary.due}
              onClick={() => {
                setPayForm((p) => ({ ...p, amount: summary.due ? String(summary.due) : p.amount }));
                setPayOpen(true);
              }}
            >
              Record payment
            </button>
          </div>
        </div>

        {msg ? (
          <p className={`caterers-msg${msg.type === 'err' ? ' caterers-msg--err' : ' caterers-msg--ok'}`}>{msg.text}</p>
        ) : null}

        <div className="caterer-kpi-grid">
          <div className="caterer-kpi">
            <em>Events</em>
            <strong>{summary.eventCount}</strong>
            <span>{summary.openCount} open</span>
          </div>
          <div className="caterer-kpi">
            <em>Billed</em>
            <strong>{formatInr(summary.billed)}</strong>
            <span>items + travel</span>
          </div>
          <div className="caterer-kpi">
            <em>Paid</em>
            <strong>{formatInr(summary.paid)}</strong>
            <span>all receipts</span>
          </div>
          <div className="caterer-kpi caterer-kpi--due">
            <em>Total due</em>
            <strong>{formatInr(summary.due)}</strong>
            <span>open balance</span>
          </div>
        </div>

        <div className="caterer-tabs" role="tablist">
          <button
            type="button"
            className={tab === 'events' ? 'is-active' : ''}
            onClick={() => setTab('events')}
          >
            Events
          </button>
          <button
            type="button"
            className={tab === 'statement' ? 'is-active' : ''}
            onClick={() => setTab('statement')}
          >
            Statement of account
          </button>
        </div>

        {tab === 'events' ? (
          <div className="card">
            <h2>Events &amp; costs</h2>
            {!events.length ? (
              <p className="no-data">No orders linked to this caterer yet.</p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Event date</th>
                      <th>Order</th>
                      <th>Venue / party</th>
                      <th>Kind</th>
                      <th>Cost</th>
                      <th>Paid</th>
                      <th>Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => (
                      <tr key={ev.id}>
                        <td>
                          {formatDateDisplay(ev.eventDate)}
                          {ev.eventTime ? ` · ${ev.eventTime}` : ''}
                        </td>
                        <td>
                          <Link to={`/orders/${ev.id}`}>{ev.orderNumber}</Link>
                          <div className="muted">{ev.category}</div>
                        </td>
                        <td>
                          <div>{ev.eventVenue || '—'}</div>
                          <div className="muted">{ev.partyName || ev.customerName || ''}</div>
                        </td>
                        <td>
                          {ev.orderKind === 'mixed'
                            ? 'Desserts + Grazing'
                            : ev.orderKind === 'grazing'
                              ? 'Grazing'
                              : 'Desserts'}
                        </td>
                        <td>
                          {formatInr(ev.totalAmount)}
                          {ev.travelExpense > 0 ? (
                            <div className="muted">incl. travel {formatInr(ev.travelExpense)}</div>
                          ) : null}
                        </td>
                        <td>{formatInr(ev.paidAmount)}</td>
                        <td className={ev.dueAmount > 0 ? 'due-amount' : ''}>{formatInr(ev.dueAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="card">
            <div className="caterer-stmt-filters">
              <h2>Statement of account</h2>
              <div className="form-row">
                <div className="form-group">
                  <label>From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={stmtFrom}
                    onChange={(e) => setStmtFrom(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={stmtTo}
                    onChange={(e) => setStmtTo(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={loadStatement}>
                    Refresh
                  </button>
                </div>
              </div>
            </div>
            {!statement?.lines?.length ? (
              <p className="no-data">No statement lines in this range.</p>
            ) : (
              <>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Ref</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.lines.map((l, idx) => (
                        <tr key={`${l.type}-${l.ref}-${idx}`}>
                          <td>{formatDateDisplay(l.date)}</td>
                          <td>
                            {l.orderId ? <Link to={`/orders/${l.orderId}`}>{l.ref}</Link> : l.ref}
                          </td>
                          <td>{l.description}</td>
                          <td>{l.debit ? formatInr(l.debit) : '—'}</td>
                          <td>{l.credit ? formatInr(l.credit) : '—'}</td>
                          <td>{formatInr(l.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="caterers-hint">
                  Closing balance: <strong>{formatInr(statement.closingBalance)}</strong>
                </p>
              </>
            )}
          </div>
        )}

        {payOpen ? (
          <div className="caterer-pay-overlay" onClick={() => !payBusy && setPayOpen(false)}>
            <div className="caterer-pay-modal card" onClick={(e) => e.stopPropagation()}>
              <h2>Record payment</h2>
              <p className="caterers-hint">
                Amount is applied FIFO against open event dues ({openEvents.length} open). Settlement is
                caterer-wise on this statement — individual events are not marked settled separately.
              </p>
              <form onSubmit={submitPayment}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount (₹) *</label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      className="form-control"
                      value={payForm.amount}
                      onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Mode</label>
                    <select
                      className="form-control"
                      value={payForm.payment_mode}
                      onChange={(e) => setPayForm({ ...payForm, payment_mode: e.target.value })}
                    >
                      {PAYMENT_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={payForm.payment_date}
                      onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    value={payForm.notes}
                    onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                {openEvents.length ? (
                  <div className="caterer-open-dues">
                    <strong>Open dues (FIFO order)</strong>
                    <ul>
                      {openEvents.map((ev) => (
                        <li key={ev.id}>
                          {ev.orderNumber} · {formatDateDisplay(ev.eventDate)} — due {formatInr(ev.dueAmount)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="no-data">No open dues to allocate.</p>
                )}
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" disabled={payBusy} onClick={() => setPayOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={payBusy || !openEvents.length}>
                    {payBusy ? 'Saving…' : 'Save payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CatererAccount;
